import { useState, useEffect } from 'react'
import { useTonConnect } from './useTonConnect'
import { Address, type TonClient } from '@ton/ton'

const MAX_RETRY_ATTEMPTS = 5
const RETRY_DELAY_MS = 5000

export function useWalletBalance(client: TonClient | undefined) {
  const { wallet } = useTonConnect()
  const [balance, setBalance] = useState<bigint | null>(null)

  useEffect(() => {
    if (!wallet || !client) {
      return
    }

    let isCancelled = false

    const fetchBalanceWithRetry = async () => {
      for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
        if (isCancelled) {
          return
        }

        try {
          const walletAddress = wallet.toString()
          const balance = await client.getBalance(Address.parse(walletAddress))
          setBalance(balance)
          return
        } catch (error) {
          if (attempt === MAX_RETRY_ATTEMPTS) {
            console.error(
              `Error fetching balance (attempt ${attempt}/${MAX_RETRY_ATTEMPTS}):`,
              error,
            )
            throw new Error(
              `Error fetching balance (attempt ${attempt}/${MAX_RETRY_ATTEMPTS})`,
            )
          }

          console.warn(
            `Error fetching balance (attempt ${attempt}/${MAX_RETRY_ATTEMPTS}), retrying in ${RETRY_DELAY_MS / 1000}s...`,
          )

          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS))
        }
      }
    }

    fetchBalanceWithRetry()

    return () => {
      isCancelled = true
    }
  }, [wallet, client])

  return balance
}
